import { normalizeComplianceStandardCode } from './complianceStandard'

export type ComplianceScopeEntry = {
  standardCode: string
  selfAssessedPercent?: number | null
  verifiedPercent?: number | null
  verifiedStatus?: string | null
  verifiedSource?: string | null
}

export function isConfirmedCompliance(entry: ComplianceScopeEntry | undefined): boolean {
  if (!entry) return false
  return (
    entry.verifiedSource === 'evidence_supported_controls' &&
    entry.verifiedPercent != null &&
    Number(entry.verifiedPercent) > 0
  )
}

export function isVerificationInProgress(entry: ComplianceScopeEntry | undefined): boolean {
  if (!entry || isConfirmedCompliance(entry)) return false
  const st = String(entry.verifiedStatus || '').toUpperCase()
  return st === 'PARTIALLY_VERIFIED' || st === 'IN_PROGRESS'
}

/** Primary compliance label for a scoped standard (confirmed → verified %, else self %, else dash). */
export function formatComplianceScopePrimary(
  entry: ComplianceScopeEntry | undefined,
  t: (key: string, fallback: string) => string,
): string {
  if (!entry) return '—'
  if (isConfirmedCompliance(entry)) {
    return `${t('complianceStatusAuthority.verified.short', 'Verified')} ${Math.round(Number(entry.verifiedPercent))}%`
  }
  if (isVerificationInProgress(entry) && entry.verifiedPercent != null && entry.verifiedPercent > 0) {
    return `${t('complianceStatusAuthority.verificationInProgress', 'Verification in progress')} (${Math.round(entry.verifiedPercent)}%)`
  }
  if (entry.selfAssessedPercent != null && entry.selfAssessedPercent > 0) {
    return `${t('complianceStatusAuthority.selfAssessed', 'Self-assessed')} ${Math.round(entry.selfAssessedPercent)}%`
  }
  return '—'
}

export function findComplianceScopeEntry(
  entries: ComplianceScopeEntry[] | undefined,
  standardCode: string,
): ComplianceScopeEntry | undefined {
  const canon = normalizeComplianceStandardCode(standardCode)
  return (entries || []).find((e) => normalizeComplianceStandardCode(e.standardCode) === canon)
}

/** Prefer entries that carry self/verified percents (operational row + executive API). */
export function mergeComplianceScopeEntries(
  ...sources: Array<ComplianceScopeEntry[] | undefined>
): ComplianceScopeEntry[] {
  const map = new Map<string, ComplianceScopeEntry>()
  for (const list of sources) {
    for (const raw of list || []) {
      const code = normalizeComplianceStandardCode(raw.standardCode)
      if (!code) continue
      const prev = map.get(code)
      const next: ComplianceScopeEntry = {
        standardCode: code,
        selfAssessedPercent: raw.selfAssessedPercent ?? prev?.selfAssessedPercent ?? null,
        verifiedPercent: raw.verifiedPercent ?? prev?.verifiedPercent ?? null,
        verifiedStatus: raw.verifiedStatus ?? prev?.verifiedStatus ?? null,
        verifiedSource: raw.verifiedSource ?? prev?.verifiedSource ?? null,
      }
      const prevHasMetric =
        prev &&
        (prev.selfAssessedPercent != null ||
          prev.verifiedPercent != null ||
          isConfirmedCompliance(prev))
      const nextHasMetric =
        next.selfAssessedPercent != null ||
        next.verifiedPercent != null ||
        isConfirmedCompliance(next)
      if (!prev || (!prevHasMetric && nextHasMetric)) {
        map.set(code, next)
      } else if (prev) {
        map.set(code, {
          ...prev,
          selfAssessedPercent: prev.selfAssessedPercent ?? next.selfAssessedPercent,
          verifiedPercent: prev.verifiedPercent ?? next.verifiedPercent,
          verifiedStatus: prev.verifiedStatus ?? next.verifiedStatus,
          verifiedSource: prev.verifiedSource ?? next.verifiedSource,
        })
      }
    }
  }
  return [...map.values()]
}
